import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaLightbulb, FaHandshake, FaRocket, FaRegStar } from 'react-icons/fa';
import './About.css';

const About = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Team members data
  // const teamMembers = [
  //   {
  //     id: 1,
  //     name: 'Harsh Patidar',
  //     position: 'CEO & Founder',
  //     bio: 'Harsh has over 5 years of experience in the tech industry and is passionate about helping businesses grow through innovative digital solutions.',
  //     image: 'https://drive.google.com/file/d/1x_GoPEep-jClAilaU_8NfHPY2NYspekt/view?usp=sharing'
  //   },
  //   {
  //     id: 1,
  //     name: 'Ashwin Chakravedi',
  //     position: 'CEO & Founder',
  //     bio: 'Ashwin has over 4 years of experience in digital marketing.',
  //     image: 'https://drive.google.com/file/d/1h1n3aPY5V1X4hxciYdHS7Gx6DRGL3SpG/view?usp=sharing'
  //   }
  // ];

  // Core values data
  const coreValues = [
    {
      id: 1,
      icon: <FaLightbulb />,
      title: 'Innovation',
      description: 'We constantly seek new and better ways to solve problems and deliver value to our clients.'
    },
    {
      id: 2,
      icon: <FaHandshake />,
      title: 'Integrity',
      description: 'We are committed to honesty, transparency, and ethical business practices in all our interactions.'
    },
    {
      id: 3,
      icon: <FaRocket />,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from code quality to customer service.'
    },
    {
      id: 4,
      icon: <FaRegStar />,
      title: 'Client Success',
      description: 'Our clients success is our success. We are dedicated to helping them achieve their goals.'
    }
  ];

  // Intersection observer hooks
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [storyRef, storyInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [teamRef, teamInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="about-page">
      {/* Page Header */}
      <section className="about-header" ref={headerRef}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8} md={10}>
              <motion.div
                initial="hidden"
                animate={headerInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <h1 className="page-title">About Us</h1>
                <p className="lead">
                  We're a team of passionate technologists, designers, and strategists dedicated to helping businesses thrive in the digital world.
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Story Section */}
      <section className="story-section section-padding" ref={storyRef}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12} className="mb-5 mb-lg-0">
              <motion.div
                initial="hidden"
                animate={storyInView ? "visible" : "hidden"}
                variants={fadeIn}
                className="story-image"
              >
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80" 
                  alt="Our team at work" 
                  className="img-fluid rounded" 
                />
              </motion.div>
            </Col>
            <Col lg={6} md={12}>
              <motion.div
                initial="hidden"
                animate={storyInView ? "visible" : "hidden"}
                variants={fadeIn}
                className="story-content"
              >
                <h2 className="section-title">Our Story</h2>
                <p>networkBuilder was founded in 2022 with a simple mission: to help businesses succeed in the digital age by providing exceptional web development and design services.</p>
                <p>What started as a small team of two passionate developers has grown into a full-service digital agency with experts in various fields, from UX design to digital marketing.</p>
                <p>Throughout our journey, we've remained committed to our core values of innovation, quality, and client satisfaction. We believe in building long-term relationships with our clients and becoming true partners in their success.</p>
                <p>Today, we're proud to have worked with over 50+ clients across various industries, helping them achieve their digital goals and grow their businesses.</p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Core Values Section */}
      <section className="values-section section-padding" ref={valuesRef}>
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <motion.div
                initial="hidden"
                animate={valuesInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <h2 className="section-title mx-auto">Our Core Values</h2>
                <p className="section-description">The principles that guide our work and our interactions with clients.</p>
              </motion.div>
            </Col>
          </Row>
          <motion.div
            initial="hidden"
            animate={valuesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row>
              {coreValues.map((value) => (
                <Col lg={3} md={6} className="mb-4" key={value.id}>
                  <motion.div variants={fadeIn}>
                    <Card className="value-card card-hover h-100">
                      <Card.Body className="text-center p-4">
                        <div className="value-icon">
                          {value.icon}
                        </div>
                        <Card.Title className="value-title my-3">{value.title}</Card.Title>
                        <Card.Text>{value.description}</Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Team Section */}
      <section className="team-section section-padding" ref={teamRef}>
        <Container>
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <motion.div
                initial="hidden"
                animate={teamInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <h2 className="section-title mx-auto">Meet Our Team</h2>
                <p className="section-description">The talented people behind our success.</p>
              </motion.div>
            </Col>
          </Row>
          <motion.div
            initial="hidden"
            animate={teamInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className='d-flex justify-content-center align-items-center'>
                <Col lg={4} md={6} className="mb-4 ">
                  <motion.div variants={fadeIn} className="shadow rounded">
                    <Card className="team-card card-hover h-100">
                      <div className="team-image-container d-flex justify-content-center align-items-center mt-2">
                        {/* <img 
                  src="https://lh3.googleusercontent.com/d/1hf9FY-7Tnyfjuf7kgWBdlkqvmCt1X1cX=w1000" 
                  alt="Our team at work" 
                  className="img-fluid rounded-circle object-fit-cover"
                  style={{ width: '250px', height: '250px' }}
                /> */}
                      </div>
                      <Card.Body className="text-center p-4">
                        <Card.Title className="team-name">Harsh Patel</Card.Title>
                        <div className="team-position mb-3">CEO & Founder</div>
                        <Card.Text><i className='text-muted'>Tech Lead | Web Developer</i><br/> Harsh has over 5 years of experience in the tech industry and is passionate about helping businesses grow through innovative digital solutions.</Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>

                <Col lg={4} md={6} className="mb-4">
                  <motion.div variants={fadeIn} className="shadow rounded">
                    <Card className="team-card card-hover h-100">
                      <div className="team-image-container d-flex justify-content-center align-items-center mt-2">
                        {/* <img 
                  src="https://lh3.googleusercontent.com/d/1h1n3aPY5V1X4hxciYdHS7Gx6DRGL3SpG=w1000" 
                  alt="Our team at work" 
                  className="img-fluid rounded-circle object-fit-cover"
                  style={{ width: '250px', height: '250px' }}
                /> */}
                      </div>
                      <Card.Body className="text-center p-4">
                        <Card.Title className="team-name">Ashwin Chakravedi</Card.Title>
                        <div className="team-position mb-3">CEO & Founder</div>
                        <Card.Text><i className='text-muted'>Content Creator | Social Media Manager | Project Manager</i> <br/> With 4+ years in tech, I create engaging content, manage social media growth, and lead projects to success.</Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
            </Row>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default About; 